/**
 * <m-cart-recommendations>
 * Renders product recommendations inside the cart drawer.
 * - "أضف" button does AJAX add-to-cart (POST /cart/add.js)
 * - After add, triggers the existing cart-drawer refresh which also re-renders
 *   this custom element via Section Rendering API (cart-drawer section).
 * - Horizontal scroll buttons for navigation through cards.
 */

class MCartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.boundHandlers = false;
  }

  connectedCallback() {
    this.track = this.querySelector("[data-reco-track]");
    if (!this.track) return;

    if (!this.boundHandlers) {
      this.boundHandlers = true;
      this.addEventListener("click", this.onClick.bind(this));
      this.addEventListener("change", this.onChange.bind(this));
    }
  }

  onChange(event) {
    const select = event.target.closest("[data-reco-variant-select]");
    if (!select) return;
    this.updatePriceDisplay(select);
  }

  /**
   * Update the visible price block when the variant select changes.
   * Reads data-price (formatted) and data-compare-at-price from the chosen option.
   */
  updatePriceDisplay(select) {
    const card = select.closest("[data-reco-card]");
    if (!card) return;
    const option = select.options[select.selectedIndex];
    if (!option) return;

    const finalEl = card.querySelector("[data-reco-price-final]");
    const compareEl = card.querySelector("[data-reco-price-compare]");

    const newPrice = option.getAttribute("data-price") || "";
    const newCompare = option.getAttribute("data-compare-at-price") || "";

    if (finalEl) finalEl.textContent = newPrice;
    if (compareEl) {
      if (newCompare) {
        compareEl.textContent = newCompare;
        compareEl.classList.remove("m:hidden");
      } else {
        compareEl.textContent = "";
        compareEl.classList.add("m:hidden");
      }
    }

    // Disable the add button if the chosen variant is unavailable
    const addBtn = card.querySelector("[data-reco-add]");
    const available = option.getAttribute("data-available") === "true";
    if (addBtn) {
      addBtn.disabled = !available;
      addBtn.classList.toggle("is-disabled", !available);
    }
  }

  onClick(event) {
    const addBtn = event.target.closest("[data-reco-add]");
    if (addBtn) {
      event.preventDefault();
      this.handleAdd(addBtn);
      return;
    }

    const prev = event.target.closest("[data-reco-prev]");
    if (prev) {
      this.scrollTrack(-1);
      return;
    }

    const next = event.target.closest("[data-reco-next]");
    if (next) {
      this.scrollTrack(1);
    }
  }

  scrollTrack(direction) {
    if (!this.track) return;
    const card = this.track.querySelector("[data-reco-card]");
    if (!card) return;
    const step = card.offsetWidth + 12;
    // RTL aware: in RTL, "next" is visually right-to-left, scrollBy still works with negative values
    const isRtl = getComputedStyle(this.track).direction === "rtl";
    const delta = (isRtl ? -1 : 1) * direction * step;
    this.track.scrollBy({ left: delta, behavior: "smooth" });
  }

  async handleAdd(button) {
    if (button.disabled) return;
    const card = button.closest("[data-reco-card]");
    if (!card) return;

    const variantInput = card.querySelector("[data-reco-variant-id]");
    const variantId = variantInput ? variantInput.value : null;
    if (!variantId) {
      this.showError(button, "Variant not found");
      return;
    }

    this.setLoading(button, true);

    try {
      const sectionId = this.getCartDrawerSectionId();
      const body = {
        items: [{ id: parseInt(variantId, 10), quantity: 1 }]
      };
      // Request the cart-drawer section back so we can re-render in one round-trip
      if (sectionId) {
        body.sections = [sectionId];
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Add failed: ${response.status} ${errText}`);
      }

      // After successful add, refresh the cart drawer via the existing mechanism
      this.triggerCartDrawerRefresh();
    } catch (err) {
      this.showError(button, err.message || "Add failed");
    } finally {
      this.setLoading(button, false);
    }
  }

  getCartDrawerSectionId() {
    // The cart-drawer section file is named "cart-drawer" → its id is "cart-drawer"
    return "cart-drawer";
  }

  triggerCartDrawerRefresh() {
    // Prefer the existing MCartDrawer instance refresh; otherwise dispatch a custom event
    const drawer = document.querySelector("m-cart-drawer");
    if (drawer && typeof drawer.onCartDrawerUpdate === "function") {
      drawer.onCartDrawerUpdate(true);
      // ensure it opens so the user sees the added item
      if (typeof drawer.open === "function") drawer.open();
      return;
    }

    // Fallback — let other listeners know
    if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
      window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate);
    } else {
      // Last resort
      window.location.reload();
    }
  }

  setLoading(button, loading) {
    if (loading) {
      button.disabled = true;
      button.classList.add("is-loading");
      const label = button.querySelector(".m-cart-reco__add-label");
      const spinner = button.querySelector(".m-cart-reco__add-spinner");
      if (label) label.classList.add("m:hidden");
      if (spinner) spinner.classList.remove("m:hidden");
    } else {
      button.disabled = false;
      button.classList.remove("is-loading");
      const label = button.querySelector(".m-cart-reco__add-label");
      const spinner = button.querySelector(".m-cart-reco__add-spinner");
      if (label) label.classList.remove("m:hidden");
      if (spinner) spinner.classList.add("m:hidden");
    }
  }

  showError(button, message) {
    // Minimal inline error indication — does not throw the whole drawer
    button.setAttribute("title", message);
    button.classList.add("has-error");
    setTimeout(() => button.classList.remove("has-error"), 2500);
    if (window.console && console.warn) {
      console.warn("[cart-recommendations]", message);
    }
  }
}

if (!customElements.get("m-cart-recommendations")) {
  customElements.define("m-cart-recommendations", MCartRecommendations);
}
